import { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    Skeleton,
    Stack,
    Button,
} from "@mui/material";
import { Link } from "react-router-dom";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { TOOLS, formatFileSize } from "../util/config";
import { colors } from "../theme/theme";
import { statsApi, commentsApi } from "../api/client";

const StatTile = ({ tool, count, last7days, totalBytes }) => {
    const Icon = tool.icon;
    return (
        <Card sx={{ height: "100%", position: "relative", overflow: "hidden" }}>
            {/* Accent stripe */}
            <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, bgcolor: tool.accent }} />
            <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                    <Box
                        sx={{
                            width: 38,
                            height: 38,
                            borderRadius: 2,
                            bgcolor: `${tool.accent}15`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: tool.accent,
                        }}
                    >
                        <Icon fontSize="small" />
                    </Box>
                    <Box flex={1}>
                        <Typography variant="overline" sx={{ color: colors.textMuted, lineHeight: 1 }}>
                            {tool.id}
                        </Typography>
                        <Typography sx={{ fontFamily: '"Outfit"', fontWeight: 600, fontSize: 15, lineHeight: 1.2 }}>
                            {tool.label}
                        </Typography>
                    </Box>
                </Stack>

                <Typography
                    sx={{
                        fontFamily: '"Outfit"',
                        fontWeight: 700,
                        fontSize: 38,
                        letterSpacing: "-0.03em",
                        lineHeight: 1,
                        color: colors.text,
                    }}
                >
                    {count.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                    total exports
                </Typography>

                <Stack direction="row" spacing={2} mt={2}>
                    <Box>
                        <Typography variant="overline" sx={{ color: colors.textMuted }}>7d</Typography>
                        <Typography sx={{ fontFamily: '"JetBrains Mono"', fontWeight: 600, fontSize: 13 }}>
                            +{last7days}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="overline" sx={{ color: colors.textMuted }}>data</Typography>
                        <Typography sx={{ fontFamily: '"JetBrains Mono"', fontWeight: 600, fontSize: 13 }}>
                            {formatFileSize(totalBytes || 0)}
                        </Typography>
                    </Box>
                </Stack>

                <Button
                    component={Link}
                    to={tool.path}
                    endIcon={<ArrowForwardIcon />}
                    size="small"
                    sx={{ mt: 2, p: 0, color: tool.accent, "&:hover": { bgcolor: "transparent", color: tool.accent } }}
                >
                    Open tool
                </Button>
            </CardContent>
        </Card>
    );
};

export const DashboardPage = () => {
    const [summary, setSummary] = useState(null);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([statsApi.summary(), commentsApi.list()])
            .then(([s, c]) => {
                setSummary(s);
                setComments(c.slice(0, 3));
            })
            .catch(() => {
                // graceful empty state
                setSummary({ totals: {}, totalExports: 0, totalBytes: 0, last7Days: 0, perTool: {} });
                setComments([]);
            })
            .finally(() => setLoading(false));
    }, []);

    const totalExports = summary?.totalExports || 0;
    const totalBytes = summary?.totalBytes || 0;
    const totalLast7 = summary?.last7Days || 0;

    return (
        <Box>
            {/* Hero header */}
            <Box mb={4}>
                <Typography variant="overline" sx={{ color: colors.textMuted }}>
                    Public dashboard
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1 }}>
                    PDF tools for everyone.
                </Typography>
                <Typography sx={{ color: colors.textMuted, maxWidth: 620 }}>
                    Free, fast, no signup. Merge, compress, convert, and edit PDFs right in your browser.
                    All exports are counted publicly so you can see what the community is using.
                </Typography>
            </Box>

            {/* Top-level KPIs */}
            <Grid container spacing={2.5} mb={4}>
                <Grid item xs={12} sm={4}>
                    <Card sx={{ bgcolor: colors.sidebarBg, color: "white", border: "none" }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="overline" sx={{ color: colors.sidebarMuted }}>
                                Total exports
                            </Typography>
                            <Typography sx={{ fontFamily: '"Outfit"', fontWeight: 700, fontSize: 44, letterSpacing: "-0.03em", lineHeight: 1 }}>
                                {loading ? <Skeleton sx={{ bgcolor: "#333" }} /> : totalExports.toLocaleString()}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={0.5} mt={1}>
                                <TrendingUpIcon sx={{ fontSize: 14, color: colors.accent }} />
                                <Typography variant="caption" sx={{ color: colors.accent, fontWeight: 600 }}>
                                    +{totalLast7} last 7 days
                                </Typography>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card sx={{ height: "100%" }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="overline" sx={{ color: colors.textMuted }}>
                                Data processed
                            </Typography>
                            <Typography sx={{ fontFamily: '"Outfit"', fontWeight: 700, fontSize: 44, letterSpacing: "-0.03em", lineHeight: 1 }}>
                                {loading ? <Skeleton /> : formatFileSize(totalBytes)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: colors.textMuted }}>
                                across all tools
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card sx={{ height: "100%" }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="overline" sx={{ color: colors.textMuted }}>
                                Community
                            </Typography>
                            <Typography sx={{ fontFamily: '"Outfit"', fontWeight: 700, fontSize: 44, letterSpacing: "-0.03em", lineHeight: 1 }}>
                                {comments.length > 0 ? `${comments.length}+` : "0"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: colors.textMuted }}>
                                comments &amp; feedback items
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Per-tool stats */}
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                Tool usage
            </Typography>
            <Grid container spacing={2.5} mb={4}>
                {TOOLS.map((tool) => (
                    <Grid item xs={12} sm={6} md={3} key={tool.id}>
                        {loading ? (
                            <Card sx={{ height: 220 }}>
                                <CardContent>
                                    <Skeleton width={120} />
                                    <Skeleton width={80} height={40} />
                                    <Skeleton />
                                </CardContent>
                            </Card>
                        ) : (
                            <StatTile
                                tool={tool}
                                count={summary?.perTool?.[tool.id]?.count || 0}
                                last7days={summary?.perTool?.[tool.id]?.last7Days || 0}
                                totalBytes={summary?.perTool?.[tool.id]?.totalBytes || 0}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>

            {/* Recent community feedback */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>Recent feedback</Typography>
                <Button component={Link} to="/feedback" endIcon={<ArrowForwardIcon />}>
                    See all
                </Button>
            </Stack>
            <Grid container spacing={2}>
                {comments.length === 0 && !loading && (
                    <Grid item xs={12}>
                        <Card>
                            <CardContent sx={{ p: 4, textAlign: "center" }}>
                                <Typography sx={{ color: colors.textMuted }}>
                                    No comments yet — be the first to share feedback.
                                </Typography>
                                <Button component={Link} to="/feedback" variant="contained" sx={{ mt: 2 }}>
                                    Leave feedback
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
                {comments.map((c) => (
                    <Grid item xs={12} md={4} key={c.id}>
                        <Card sx={{ height: "100%" }}>
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                    <Chip
                                        label={c.toolId || "general"}
                                        size="small"
                                        sx={{
                                            fontFamily: '"JetBrains Mono"',
                                            fontSize: 10,
                                            height: 20,
                                            bgcolor: colors.surfaceAlt,
                                        }}
                                    />
                                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                                        {c.authorName}
                                    </Typography>
                                </Stack>
                                <Typography sx={{ fontSize: 14, lineHeight: 1.6, color: colors.text }}>
                                    {c.body.length > 140 ? c.body.slice(0, 140) + "…" : c.body}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};
